import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { FileType } from 'src/models/file';
import { IService, ServiceCategory, WeeklySchedule } from '.';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  service?: IService | null;
}

/* ---------- helpers ---------- */
const categoryItems: Record<string, string> = Object.fromEntries(
  Object.values(ServiceCategory).map((c) => [c, c])
);

type LocalNames = { en: string; ar: string; fr: string };
type LocalDescriptions = { en: string; ar: string; fr: string };
type LocalRequirements = { en: string; ar: string; fr: string };

const toTime = (value?: string) => value ?? '';
const toVal = (value?: string | number) => value ?? '';

const buildWeeklyScheduleJson = (data: Record<string, any>): string => {
  const makeDay = (open?: string, close?: string) => {
    const valid = !!(open && close);
    return {
      open: valid,
      windows: valid ? [{ start: open, end: close }] : []
    };
  };

  const weeklySchedule: WeeklySchedule = {
    monday: makeDay(data.mondayOpen, data.mondayClose),
    tuesday: makeDay(data.tuesdayOpen, data.tuesdayClose),
    wednesday: makeDay(data.wednesdayOpen, data.wednesdayClose),
    thursday: makeDay(data.thursdayOpen, data.thursdayClose),
    friday: makeDay(data.fridayOpen, data.fridayClose),
    saturday: makeDay(data.saturdayOpen, data.saturdayClose),
    sunday: makeDay(data.sundayOpen, data.sundayClose)
  };

  return JSON.stringify(weeklySchedule);
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'clean']
  ]
};

const ACCEPTED_ICONS = [FileType.png, FileType.jpeg, FileType.jpg, FileType.svg];

type TabValue = 'en' | 'ar' | 'fr' | 'hours' | 'other';

/* ---------- component ---------- */
const AddOrEditServiceDialog: React.FC<Props> = ({ open, onClose, onSuccess, service }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isEdit = !!service;

  // tabs
  const [tab, setTab] = useState<TabValue>('en');
  const handleTab = (_: React.SyntheticEvent, v: TabValue) => setTab(v);

  // localized fields
  const [name, setName] = useState<LocalNames>({ en: '', ar: '', fr: '' });
  const [desc, setDesc] = useState<LocalDescriptions>({ en: '', ar: '', fr: '' });
  const [req, setReq] = useState<LocalRequirements>({ en: '', ar: '', fr: '' });
  const [tos, setTos] = useState<LocalRequirements>({ en: '', ar: '', fr: '' });

  // others
  const [icon, setIcon] = useState<File | null>(null);
  const [iconUrl, setIconUrl] = useState<string>('');
  const [category, setCategory] = useState<ServiceCategory>(ServiceCategory.Other);

  const [feeAmount, setFeeAmount] = useState<number | ''>('');
  const [feeCurrency, setFeeCurrency] = useState<string>('USD');

  const [serviceTimeMinutes, setServiceTimeMinutes] = useState<number>(30);
  const [maxAppointments, setMaxAppointments] = useState<number | ''>('');
  const [maxAdvanceDays, setMaxAdvanceDays] = useState<number>(60);

  // hours
  const [hours, setHours] = useState<Record<string, { open: string; close: string }>>({
    monday: { open: '', close: '' },
    tuesday: { open: '', close: '' },
    wednesday: { open: '', close: '' },
    thursday: { open: '', close: '' },
    friday: { open: '', close: '' },
    saturday: { open: '', close: '' },
    sunday: { open: '', close: '' }
  });

  // hydrate
  useEffect(() => {
    if (!service) {
      setName({ en: '', ar: '', fr: '' });
      setDesc({ en: '', ar: '', fr: '' });
      setReq({ en: '', ar: '', fr: '' });
      setTos({ en: '', ar: '', fr: '' });
      setIcon(null);
      setIconUrl('');
      setCategory(ServiceCategory.Other);
      setFeeAmount('');
      setFeeCurrency('USD');
      setServiceTimeMinutes(30);
      setMaxAppointments('');
      setMaxAdvanceDays(60);
      setHours({
        monday: { open: '', close: '' },
        tuesday: { open: '', close: '' },
        wednesday: { open: '', close: '' },
        thursday: { open: '', close: '' },
        friday: { open: '', close: '' },
        saturday: { open: '', close: '' },
        sunday: { open: '', close: '' }
      });
      setTab('en');
      return;
    }

    setName({
      en: service.name?.en ?? '',
      ar: service.name?.ar ?? '',
      fr: service.name?.fr ?? ''
    });
    setDesc({
      en: service.description?.en ?? '',
      ar: service.description?.ar ?? '',
      fr: service.description?.fr ?? ''
    });
    setReq({
      en: service.requirements?.en ?? '',
      ar: service.requirements?.ar ?? '',
      fr: service.requirements?.fr ?? ''
    });
    setTos({
      en: service.termsOfServices?.en ?? '',
      ar: service.termsOfServices?.ar ?? '',
      fr: service.termsOfServices?.fr ?? ''
    });
    setIcon(null);
    setIconUrl(service.iconUrl ?? '');
    setCategory(service.category ?? ServiceCategory.Other);

    setFeeAmount(service.fee?.amount ?? '');
    setFeeCurrency(service.fee?.currency ?? 'USD');

    setServiceTimeMinutes(service.serviceTimeMinutes ?? 30);
    setMaxAppointments(service.maxAppointmentsPerDay ?? '');
    setMaxAdvanceDays(service.maxAdvanceDays ?? 60);

    const ws = service.weeklySchedule;
    const get = (day: keyof WeeklySchedule, t: 'start' | 'end') =>
      ws?.[day]?.open && ws?.[day]?.windows?.length ? ws[day].windows[0][t] : '';

    setHours({
      monday: { open: toTime(get('monday', 'start')), close: toTime(get('monday', 'end')) },
      tuesday: { open: toTime(get('tuesday', 'start')), close: toTime(get('tuesday', 'end')) },
      wednesday: { open: toTime(get('wednesday', 'start')), close: toTime(get('wednesday', 'end')) },
      thursday: { open: toTime(get('thursday', 'start')), close: toTime(get('thursday', 'end')) },
      friday: { open: toTime(get('friday', 'start')), close: toTime(get('friday', 'end')) },
      saturday: { open: toTime(get('saturday', 'start')), close: toTime(get('saturday', 'end')) },
      sunday: { open: toTime(get('sunday', 'start')), close: toTime(get('sunday', 'end')) }
    });
    setTab('en');
  }, [service]);

  const DayRow: React.FC<{ day: keyof WeeklySchedule; label: string }> = useCallback(({ day, label }) => {
    const value = hours[day];
    return (
      <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
        <Grid
          item
          xs={12}
          md={3}
          sx={{ display: 'flex', alignItems: 'center', minHeight: 56 }}
        >
          <Typography variant="subtitle2">{label}</Typography>
        </Grid>

        <Grid item xs={6} md={4}>
          <TextField
            fullWidth
            type="time"
            size="medium"
            label="Open"
            value={value.open}
            onChange={(e) =>
              setHours((s) => ({ ...s, [day]: { ...s[day], open: e.target.value } }))
            }
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={6} md={4}>
          <TextField
            fullWidth
            type="time"
            size="medium"
            label="Close"
            value={value.close}
            onChange={(e) =>
              setHours((s) => ({ ...s, [day]: { ...s[day], close: e.target.value } }))
            }
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    );
  }, [hours]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.toLowerCase().split('.').pop() || '';
    if (!Object.values(ACCEPTED_ICONS).some((t) => t.endsWith(ext))) {
      dispatch(showAlert({ type: 'error', message: 'Unsupported icon type.' }));
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      dispatch(showAlert({ type: 'error', message: 'Max icon size is 5MB.' }));
      return;
    }
    setIcon(f);
  };

  const submit = useCallback(async () => {
    // Validate icon is provided
    if (!icon && !iconUrl) {
      dispatch(showAlert({ type: 'error', message: 'Please upload a service icon before submitting.' }));
      return;
    }

    const form = new FormData();

    form.append('En_Name', name.en);
    form.append('Ar_Name', name.ar);
    form.append('Fr_Name', name.fr);

    form.append('En_Description', desc.en);
    form.append('Ar_Description', desc.ar);
    form.append('Fr_Description', desc.fr);

    if (req.en) form.append('En_requirements', req.en);
    if (req.ar) form.append('Ar_requirements', req.ar);
    if (req.fr) form.append('Fr_requirements', req.fr);

    if (icon) form.append('Icon', icon);
    else if (isEdit && iconUrl) form.append('IconUrl', iconUrl);

    form.append('Category', category || ServiceCategory.Other);
    if (feeAmount !== '' && feeAmount !== null) form.append('FeeAmount', String(feeAmount));
    if (feeCurrency) form.append('FeeCurrency', feeCurrency);

    form.append('ServiceTimeMinutes', String(serviceTimeMinutes));
    if (maxAppointments !== '' && maxAppointments !== null)
      form.append('MaxAppointmentsPerDay', String(maxAppointments));
    form.append('MaxAdvanceDays', String(maxAdvanceDays ?? 60));

    const weeklyScheduleJson = buildWeeklyScheduleJson({
      mondayOpen: hours.monday.open, mondayClose: hours.monday.close,
      tuesdayOpen: hours.tuesday.open, tuesdayClose: hours.tuesday.close,
      wednesdayOpen: hours.wednesday.open, wednesdayClose: hours.wednesday.close,
      thursdayOpen: hours.thursday.open, thursdayClose: hours.thursday.close,
      fridayOpen: hours.friday.open, fridayClose: hours.friday.close,
      saturdayOpen: hours.saturday.open, saturdayClose: hours.saturday.close,
      sundayOpen: hours.sunday.open, sundayClose: hours.sunday.close
    });
    form.append('WeeklyScheduleJson', weeklyScheduleJson);

    const endpoint = isEdit ? 'api/Service/update' : 'api/Service/create';
    if (isEdit && service?.id) form.append('Id', service.id);

    const res = await DataService.postForm(endpoint, form);
    if (res.ok) {
      onSuccess();
    } else {
      try {
        const r = await res.json();
        dispatch(showAlert({ type: 'error', message: r?.message ?? 'Failed to save service.' }));
      } catch {
        dispatch(showAlert({ type: 'error', message: 'Failed to save service.' }));
      }
    }
  }, [
    name, desc, req, icon, iconUrl, category, feeAmount, feeCurrency,
    serviceTimeMinutes, maxAppointments, maxAdvanceDays, hours, isEdit, service?.id, dispatch, onSuccess
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: 3,
          width: '90vw',
          maxWidth: 1200
        }
      }}
    >
      <DialogTitle
        sx={{
          position: 'relative',
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          py: 1.5,
          pl: 2,
          pr: 2
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isEdit ? 'Edit Service' : 'Add Service'}
        </Typography>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.primary.contrastText
          }}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3, px: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt:2, mb: 3 }}>
          <Tabs
            value={tab}
            onChange={handleTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minWidth: 100,
                fontWeight: 600
              }
            }}
          >
            <Tab label="English" value="en" />
            <Tab label="العربية" value="ar" />
            <Tab label="Français" value="fr" />
            <Tab label="Operating Hours" value="hours" />
            <Tab label="Other Properties" value="other" />
          </Tabs>
        </Box>

        {/* TAB: English */}
        {tab === 'en' && (
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Name (English)"
              value={name.en}
              onChange={(e) => setName((s) => ({ ...s, en: e.target.value }))}
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description (English)"
              value={desc.en}
              onChange={(e) => setDesc((s) => ({ ...s, en: e.target.value }))}
            />

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Requirements (English)
              </Typography>
              <ReactQuill
                theme="snow"
                value={req.en}
                onChange={(html) => setReq((s) => ({ ...s, en: html }))}
                modules={quillModules}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Use bullets, bold/italic, colors, alignment, and links. HTML will be saved as-is.
              </Typography>
            </Box>

             <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Terms Of Service (English)
              </Typography>
              <ReactQuill
                theme="snow"
                value={tos.en}
                onChange={(html) => setTos((s) => ({ ...s, en: html }))}
                modules={quillModules}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Use bullets, bold/italic, colors, alignment, and links. HTML will be saved as-is.
              </Typography>
            </Box>
          </Stack>
        )}

        {/* TAB: Arabic */}
        {tab === 'ar' && (
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="الاسم (Name)"
              value={name.ar}
              onChange={(e) => setName((s) => ({ ...s, ar: e.target.value }))}
              inputProps={{ dir: 'rtl' }}
              sx={{ '& .MuiInputBase-input': { textAlign: 'right' } }}
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="الوصف (Description)"
              value={desc.ar}
              onChange={(e) => setDesc((s) => ({ ...s, ar: e.target.value }))}
              inputProps={{ dir: 'rtl' }}
              sx={{ '& .MuiInputBase-input': { textAlign: 'right' } }}
            />

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                المتطلبات (Requirements)
              </Typography>
              <ReactQuill
                theme="snow"
                value={req.ar}
                onChange={(html) => setReq((s) => ({ ...s, ar: html }))}
                modules={quillModules}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                استخدم النقاط، النص العريض/المائل، الألوان، المحاذاة، والروابط.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                شروط الخدمة (Terms of Service)
              </Typography>
              <ReactQuill
                theme="snow"
                value={tos.ar}
                onChange={(html) => setTos((s) => ({ ...s, ar: html }))}
                modules={quillModules}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                استخدم النقاط، النص العريض/المائل، الألوان، المحاذاة، والروابط.
              </Typography>
            </Box>
          </Stack>
        )}

        {/* TAB: French */}
        {tab === 'fr' && (
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Nom (Name)"
              value={name.fr}
              onChange={(e) => setName((s) => ({ ...s, fr: e.target.value }))}
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description"
              value={desc.fr}
              onChange={(e) => setDesc((s) => ({ ...s, fr: e.target.value }))}
            />

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Exigences (Requirements)
              </Typography>
              <ReactQuill
                theme="snow"
                value={req.fr}
                onChange={(html) => setReq((s) => ({ ...s, fr: html }))}
                modules={quillModules}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Utilisez des puces, du texte en gras/italique, des couleurs, l'alignement et des liens.
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Conditions d'utilisation (Terms of Service)
              </Typography>
              <ReactQuill
                theme="snow"
                value={tos.fr}
                onChange={(html) => setTos((s) => ({ ...s, fr: html }))}
                modules={quillModules}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Utilisez des puces, du texte en gras/italique, des couleurs, l'alignement et des liens.
              </Typography>
            </Box>
          </Stack>
        )}

        {/* TAB: Operating Hours */}
        {tab === 'hours' && (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Set the operating hours for each day of the week
            </Typography>
            <DayRow day="monday" label="Monday" />
            <DayRow day="tuesday" label="Tuesday" />
            <DayRow day="wednesday" label="Wednesday" />
            <DayRow day="thursday" label="Thursday" />
            <DayRow day="friday" label="Friday" />
            <DayRow day="saturday" label="Saturday" />
            <DayRow day="sunday" label="Sunday" />
          </Stack>
        )}

        {/* TAB: Other Properties */}
        {tab === 'other' && (
          <Stack spacing={3}>
            {/* Icon Upload */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Service Icon <Typography component="span" color="error">*</Typography>
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', mb: 1 }}>
                <Button variant="outlined" startIcon={<UploadFileIcon />} component="label">
                  Upload Icon
                  <input hidden type="file" accept=".png,.jpg,.jpeg,.svg" onChange={handleFile} />
                </Button>
                {icon && <Chip label={icon.name} variant="outlined" />}
                {!icon && iconUrl && <Chip label="Using existing icon" color="success" variant="outlined" />}
                {!icon && !iconUrl && <Chip label="Required" color="error" variant="outlined" size="small" />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Accepted: PNG, JPG, JPEG, SVG — max 5MB (Required)
              </Typography>
            </Box>

            <Divider />

            {/* Category */}
            <Box>
              <InputLabel shrink sx={{ mb: 1, fontWeight: 600 }}>Category</InputLabel>
              <Select
                fullWidth
                value={category}
                onChange={(e) => setCategory(e.target.value as ServiceCategory)}
              >
                {Object.keys(categoryItems).map((k) => (
                  <MenuItem key={k} value={k}>{categoryItems[k]}</MenuItem>
                ))}
              </Select>
            </Box>

            <Divider />

            {/* Service Settings */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Service Settings
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Service Duration (minutes)"
                  value={serviceTimeMinutes}
                  onChange={(e) => setServiceTimeMinutes(Number(e.target.value || 0))}
                  inputProps={{ min: 1, max: 480 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Appointments / Day"
                  value={toVal(maxAppointments)}
                  onChange={(e) => setMaxAppointments(e.target.value === '' ? '' : Number(e.target.value))}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Advance Days"
                  value={maxAdvanceDays}
                  onChange={(e) => setMaxAdvanceDays(Number(e.target.value || 0))}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>

            <Divider />

            {/* Fee Settings */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Fee Settings
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Fee Amount"
                  value={toVal(feeAmount)}
                  onChange={(e) => setFeeAmount(e.target.value === '' ? '' : Number(e.target.value))}
                  inputProps={{ min: 0, step: '0.01' }}
                  placeholder="e.g., 50.00"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fee Currency"
                  value={feeCurrency}
                  onChange={(e) => setFeeCurrency(e.target.value)}
                  placeholder="e.g., USD"
                />
              </Grid>
            </Grid>
          </Stack>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={submit}
            variant="contained"
            disableElevation
            disabled={!icon && !iconUrl}
          >
            {isEdit ? 'Update Service' : 'Create Service'}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default AddOrEditServiceDialog;
